import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import {
  disksQuery,
  type DeviceDisksPage,
  type DiskAttribute,
  type DiskPartition,
  type DiskPieSlice,
  type SmartBanner,
  type SmartStorage,
  type SummaryDisk,
} from '../api/devices'
import { Spinner } from '../ui/Spinner'
import type { ScreenProps } from './screens'

/**
 * Fila Disk Management - proactivitee/devices/details/hddDetails.php si
 * gestul din hddDetails-1.js: bannerul SMART in patru stari, discul cu
 * placinta, atributele sub chevron, cartile de partitie cu easyPieChart.
 */

/** convert_bytes() din conversion_helper.php: IEC 1024, '%01.2f %s'. */
export function convertBytes(bytes: number | string | null | undefined): string {
  const n = Number(bytes)
  const value = Number.isFinite(n) ? n : 0
  const units = ['B', 'KB', 'MB', 'GB', 'TB', 'PB']
  const mod = 1024
  const power = value > 0 ? Math.min(Math.floor(Math.log(value) / Math.log(mod)), units.length - 1) : 0
  return `${(value / Math.pow(mod, power)).toFixed(2)} ${units[power]}`
}

const AM_COLORS = ['#FF6600', '#FCD202', '#B0DE09', '#0D8ECF', '#2A0CD0', '#CD0D74', '#CC0000', '#00CC00', '#0000CC', '#DDDDDD']

export function DeviceDiskManagement({ id }: ScreenProps) {
  const { data, error } = useQuery(disksQuery(id))
  if (error) {
    return (
      <div className="alert alert-danger" role="alert">
        Something went wrong: {error.message}
      </div>
    )
  }
  if (!data) return <Spinner />
  return <DiskPage page={data} />
}

function DiskPage({ page }: { page: DeviceDisksPage }) {
  return (
    <>
      {page.failed !== '' && (
        <div className="alert alert-warning">
          <i className="fas fa-exclamation-circle" aria-hidden="true"></i> {page.failed}
        </div>
      )}
      {page.storages.length > 0 ? (
        <>
          {page.banner && <SmartBannerBlock banner={page.banner} />}
          {page.storages.map((storage) => (
            <SmartDisk key={storage.ID} storage={storage} />
          ))}
        </>
      ) : page.summary.length > 0 ? (
        <SummaryFallback disks={page.summary} />
      ) : (
        <div className="alert alert-info">
          <strong>
            <i className="fas fa-info-circle"></i>
          </strong>{' '}
          The agent has not reported any drive for this machine yet - neither the disks with their partitions, nor a
          hardware summary. Nothing here says the machine has no disks; it says nothing has arrived about them.
        </div>
      )}
    </>
  )
}

function SmartBannerBlock({ banner }: { banner: SmartBanner }) {
  if (banner.state === 'errors') {
    return (
      <div className="dm-smart dm-smart-bad">
        <h4>
          <i className="fas fa-exclamation-triangle"></i>
          SMART errors
          <span className="dm-smart-count">{banner.count}</span>
        </h4>
        {banner.disks_bad.length > 0 && (
          <p className="dm-smart-disks">
            The drive itself reports as not healthy: <strong>{banner.disks_bad.join(', ')}</strong>.
          </p>
        )}
        {banner.failures.length > 0 && (
          <table className="table table-striped dm-smart-table">
            <thead>
              <tr>
                <th className="a-left">Disk</th>
                <th className="a-left">Attribute below threshold</th>
                <th className="a-left">Current</th>
                <th className="a-left">Worst</th>
                <th className="a-left">Threshold</th>
                <th className="a-left">Data</th>
              </tr>
            </thead>
            <tbody>
              {banner.failures.map((bad, i) => (
                <tr key={`${bad.disk}-${bad.name}-${i}`}>
                  <td>{bad.disk}</td>
                  <td>
                    <strong>{bad.name}</strong>
                  </td>
                  <td>{String(bad.current)}</td>
                  <td>{String(bad.worst)}</td>
                  <td>{String(bad.threshold)}</td>
                  <td>{String(bad.data)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <p className="dm-smart-hint">
          An attribute under its threshold is the drive saying it is wearing out. It is not a prediction of the hour it
          fails - it is the reason to have the data somewhere else before it does.
        </p>
      </div>
    )
  }
  if (banner.state === 'ok') {
    return (
      <p className="dm-smart dm-smart-ok">
        <i className="fas fa-check-circle"></i>
        No SMART attribute is below its threshold ({banner.checked} checked across {banner.drives} drive
        {banner.drives === 1 ? '' : 's'}).
      </p>
    )
  }
  if (banner.state === 'unsupported') {
    return (
      <p className="dm-smart dm-smart-warn">
        <i className="fas fa-question-circle"></i>
        These drives say they do not support SMART: <strong>{banner.unsupported.join(', ')}</strong>. Usual on virtual
        disks and behind some RAID controllers - so nothing here says whether they are healthy.
      </p>
    )
  }
  return (
    <p className="dm-smart dm-smart-warn">
      <i className="fas fa-question-circle"></i>
      SMART has not reported anything for these drives yet, so nothing here says whether they are healthy.
    </p>
  )
}

function SmartStatus({ isOk }: { isOk: number }) {
  if (isOk === 1) {
    return (
      <>
        <i className="color-green fas fa-check-circle"></i> healthy
      </>
    )
  }
  if (isOk === 3) {
    return (
      <>
        <i className="color-yellow fas fa-question-circle"></i> not supported by this drive
      </>
    )
  }
  return (
    <>
      <i className="color-red fas fa-minus-circle"></i> the drive reports a problem
    </>
  )
}

function SmartDisk({ storage }: { storage: SmartStorage }) {
  const attributes = storage.attributes ?? []
  const badHere = attributes.filter((a) => emptyOk(a.is_ok)).length
  const [open, setOpen] = useState(badHere > 0)
  const parts = storage.partitions ?? []
  const temp = Number(storage.temperature) || 0

  return (
    <div className="dm-disk">
      <h4>
        <i className="far fa-hdd" aria-hidden="true"></i> {storage.name}
      </h4>
      <hr />

      <div className="row">
        <div className="col-md-5">
          <div
            style={{ width: '100%', height: 240 }}
            className="hdd_chart"
            id={`hdd_chart_${storage.ID}`}
            data-hdd={storage.ID}
            data-pie={JSON.stringify(storage.pie)}
          >
            <HddPie slices={storage.pie} />
          </div>
        </div>
        <div className="col-md-7">
          <table className="mlist-details" width="100%">
            <tbody>
              <tr>
                <td width="150">
                  <b>Index: </b>
                </td>
                <td>{String(storage.index)}</td>
              </tr>
              <tr>
                <td width="150">
                  <b>Size: </b>
                </td>
                <td>{convertBytes(storage.size)}</td>
              </tr>
              <tr>
                <td width="150">
                  <b>Unallocated space: </b>
                </td>
                <td>{convertBytes(storage.unallocated_space)}</td>
              </tr>
              <tr>
                <td width="150">
                  <b>Temperature: </b>
                </td>
                <td>
                  {temp > 0 ? (
                    <>
                      <i className="wi wi-thermometer"></i> {temp}&deg;C
                    </>
                  ) : (
                    'n/a'
                  )}
                </td>
              </tr>
              <tr>
                <td width="150">
                  <b>Status: </b>
                </td>
                <td>{storage.status}</td>
              </tr>
              <tr>
                <td width="150">
                  <b>SMART: </b>
                </td>
                <td>
                  <SmartStatus isOk={Number(storage.is_ok)} />
                </td>
              </tr>
            </tbody>
          </table>

          {attributes.length > 0 ? (
            <span
              className="dm-attr-toggle"
              data-target={`dm-attr-${storage.ID}`}
              onClick={() => setOpen((v) => !v)}
            >
              <i className={`fas ${open ? 'fa-chevron-up' : 'fa-chevron-down'}`}></i>
              SMART attributes ({attributes.length}
              {badHere > 0 ? `, ${badHere} below threshold` : ''})
            </span>
          ) : (
            <p className="dm-smart-hint" style={{ marginTop: 8 }}>
              <i className="fas fa-question-circle"></i>
              No SMART attribute was reported for this drive.
            </p>
          )}
        </div>
      </div>

      {attributes.length > 0 && (
        <div className={`dm-attr-wrap${open ? ' is-open' : ''}`} id={`dm-attr-${storage.ID}`}>
          <AttributeTable attributes={attributes} />
        </div>
      )}

      <div className="row" style={{ margin: '20px -15px' }}>
        {parts.length === 0 && (
          <div className="col-md-12">
            <p className="dm-smart-hint">
              <i className="fas fa-question-circle"></i>
              No partition was reported for this drive.
            </p>
          </div>
        )}
        {parts.map((partition, i) => (
          <PartitionCard key={`${partition.name}-${i}`} partition={partition} />
        ))}
      </div>
    </div>
  )
}

function AttributeTable({ attributes }: { attributes: DiskAttribute[] }) {
  return (
    <table className="table table-striped table-hover">
      <thead>
        <tr>
          <th className="a-left">Disk property</th>
          <th className="a-left">Current</th>
          <th className="a-left">Worst</th>
          <th className="a-left">Threshold</th>
          <th className="a-left">Data</th>
          <th className="a-left">Is OK</th>
        </tr>
      </thead>
      <tbody>
        {attributes.map((attribute, i) => {
          const bad = emptyOk(attribute.is_ok)
          return (
            <tr key={`${attribute.name}-${i}`} className={bad ? 'dm-attr-bad' : undefined}>
              <td>{String(attribute.name)}</td>
              <td>{String(attribute.current)}</td>
              <td>{String(attribute.worst)}</td>
              <td>{String(attribute.treshold)}</td>
              <td>{String(attribute.data)}</td>
              <td>
                {bad ? (
                  <i className="color-red fas fa-minus-circle"></i>
                ) : (
                  <i className="color-green fas fa-check-circle"></i>
                )}
              </td>
            </tr>
          )
        })}
      </tbody>
    </table>
  )
}

function PartitionCard({ partition }: { partition: DiskPartition }) {
  const pSize = Number(partition.size) || 0
  const pFree = Number(partition.free_space) || 0
  const pUsed = pSize > pFree ? pSize - pFree : 0
  const pFreePct = pSize > 0 ? Math.round((pFree / pSize) * 100) : 0
  const fc = partition.forecast

  return (
    <div className="col-md-3">
      <div style={{ backgroundColor: 'rgb(143, 157, 179)', padding: 10, minHeight: 110 }}>
        <FreePie percent={pFreePct} />
        <div style={{ float: 'right' }}>
          <h5 style={{ color: '#fff', textAlign: 'right', marginBottom: 10 }}>
            <i className="fa fa-chart-pie"></i> {partition.name}
          </h5>
          <ul>
            {partition.file_system !== '' && <li className="color-white aright">{partition.file_system}</li>}
            <li className="color-white aright">{convertBytes(pSize)} total</li>
            <li className="color-white aright">{convertBytes(pUsed)} used</li>
            <li className="color-white aright">{convertBytes(pFree)} free</li>
            {fc && fc.label !== '' && (
              <li className="color-white aright">
                {fc.label}
                {fc.rate !== '' && (
                  <>
                    {' '}
                    &middot; {fc.rate}
                  </>
                )}
              </li>
            )}
            {partition.volume_serial_number !== '' && (
              <li className="color-white aright">{partition.volume_serial_number} (volume SN)</li>
            )}
          </ul>
        </div>
      </div>
    </div>
  )
}

function SummaryFallback({ disks }: { disks: SummaryDisk[] }) {
  return (
    <>
      <p className="dm-smart dm-smart-none">
        <i className="fas fa-info-circle"></i>
        The agent has not sent SMART details for this machine, so there are no attributes and no partitions here. These
        are the drives it did report.
      </p>
      <div className="row">
        {disks.map((hdd, i) => (
          <div className="col-md-6" key={`${hdd.index}-${i}`}>
            <div className="dm-disk">
              <h4>
                <i className="far fa-hdd" aria-hidden="true"></i> {String(hdd.index)}
              </h4>
              <hr />
              <table className="mlist-details" width="100%">
                <tbody>
                  <tr>
                    <td width="150">
                      <b>Size: </b>
                    </td>
                    <td>{convertBytes(hdd.size)}</td>
                  </tr>
                  <tr>
                    <td width="150">
                      <b>Unallocated space: </b>
                    </td>
                    <td>{convertBytes(hdd.unallocated_space)}</td>
                  </tr>
                  <tr>
                    <td width="150">
                      <b>SMART: </b>
                    </td>
                    <td>
                      <SmartStatus isOk={Number(hdd.is_ok)} />
                    </td>
                  </tr>
                  <tr>
                    <td width="150">
                      <b>Status: </b>
                    </td>
                    <td>{hdd.status}</td>
                  </tr>
                  <tr>
                    <td width="150">
                      <b>Temperature: </b>
                    </td>
                    <td>{hdd.current_temperature ? `${hdd.current_temperature}\u00b0C` : 'n/a'}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        ))}
      </div>
    </>
  )
}

/** empty() din PHP: 0, '0', '', null. */
function emptyOk(value: string | number | null | undefined): boolean {
  return value === null || value === undefined || value === '' || value === 0 || value === '0'
}

/** easyPieChart(size 70, bar #FFAA00, lineWidth 10), ca performanceHtml dar pe 70. */
export function FreePie({ percent }: { percent: number }) {
  const size = 70
  const lineWidth = 10
  const r = (size - lineWidth) / 2
  const c = 2 * Math.PI * r
  const dash = (Math.max(0, Math.min(100, percent)) / 100) * c
  return (
    <div className="easychart partitions free_space" data-percent={percent} style={{ width: size, height: size }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} aria-hidden="true" style={{ transform: 'rotate(-90deg)' }}>
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#f2f2f2" strokeWidth={lineWidth}></circle>
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke="#FFAA00"
          strokeWidth={lineWidth}
          strokeLinecap="round"
          strokeDasharray={`${dash.toFixed(3)} ${c.toFixed(3)}`}
        ></circle>
      </svg>
      <span className="percent_partitions">{Math.round(percent)}%</span>
    </div>
  )
}

/** Placinta AmCharts din data-pie: valueField size, titleField name, theme light. */
export function HddPie({ slices }: { slices: DiskPieSlice[] }) {
  const total = slices.reduce((sum, s) => sum + (Number(s.size) || 0), 0)
  if (total <= 0 || slices.length === 0) return null

  const cx = 120
  const cy = 120
  const r = 80
  let angle = -Math.PI / 2
  const paths = slices.map((s, i) => {
    const value = Number(s.size) || 0
    const sweep = (value / total) * Math.PI * 2
    const start = angle
    const end = angle + sweep
    angle = end
    const large = sweep > Math.PI ? 1 : 0
    const x1 = cx + r * Math.cos(start)
    const y1 = cy + r * Math.sin(start)
    const x2 = cx + r * Math.cos(end)
    const y2 = cy + r * Math.sin(end)
    const mid = start + sweep / 2
    const lx = cx + (r + 22) * Math.cos(mid)
    const ly = cy + (r + 22) * Math.sin(mid)
    const pct = (value / total) * 100
    return {
      d: `M ${cx} ${cy} L ${x1} ${y1} A ${r} ${r} 0 ${large} 1 ${x2} ${y2} Z`,
      color: AM_COLORS[i % AM_COLORS.length],
      name: s.name,
      pct,
      lx,
      ly,
    }
  })

  return (
    <svg width="100%" height="240" viewBox="0 0 240 240" role="img" aria-label="Disk partitions">
      {paths.map((p) => (
        <g key={p.name}>
          <path
            d={p.d}
            fill={p.color}
            stroke="#fff"
            strokeWidth="1"
          >
            <title>
              {p.name}
              {'\n'}({p.pct.toFixed(2)}%)
            </title>
          </path>
          <text x={p.lx} y={p.ly} textAnchor="middle" dominantBaseline="middle" fontSize="11" fill="#333">
            {p.name}
          </text>
        </g>
      ))}
    </svg>
  )
}
